/**
 * The backstop, not the mechanism.
 *
 * Stale chunks are handled where they happen — see utils/lazyWithRetry, which retries and
 * then reloads once. What reaches here is either a chunk failure that survived both (so
 * reloading was not the answer and saying so is better than looping), or an ordinary
 * render error, which previously produced a white page because the app had no boundary
 * anywhere.
 */
import React from 'react';
import { isChunkError, markChunksHealthy } from '../utils/lazyWithRetry';

export default class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidMount() {
    /*
     * Rendering at all means this tab is serving current assets, so the reload guard can
     * be released and the next deploy gets the same one free recovery this one did.
     */
    markChunksHealthy();

    /*
     * Vite reports a failed module preload on the window rather than through React, so a
     * stale chunk can fail before any component has had a chance to throw.
     */
    this.onPreloadError = (e) => {
      const error = e?.payload || e;
      if (isChunkError(error)) this.setState({ error });
    };
    window.addEventListener('vite:preloadError', this.onPreloadError);
  }

  componentWillUnmount() {
    window.removeEventListener('vite:preloadError', this.onPreloadError);
  }

  componentDidCatch(error, info) {
    // Kept for whoever is reading the console; the person gets the panel below.
    console.error('[app] render failed:', error, info?.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const stale = isChunkError(error);
    return (
      <div style={{ padding: 32, maxWidth: 560, margin: '48px auto', textAlign: 'center' }}>
        <h3 style={{ margin: '0 0 8px' }}>
          {stale ? 'This page could not finish loading' : 'Something went wrong on this page'}
        </h3>
        <p style={{ color: '#4A6478', lineHeight: 1.5, margin: '0 0 18px' }}>
          {stale
            ? 'It may be a connection problem. Reload to try again, and tell IT if it keeps happening.'
            : 'The rest of the site is unaffected. Reload this page, and tell IT if it keeps happening.'}
        </p>
        <button
          type="button"
          onClick={() => { markChunksHealthy(); window.location.reload(); }}
          style={{ padding: '10px 18px', borderRadius: 8, border: 0, background: '#0673AF', color: '#fff', cursor: 'pointer' }}
        >
          Reload the page
        </button>
      </div>
    );
  }
}
